import React, { useState } from 'react';
import { Link } from 'gatsby';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { useSiteConfig } from '@querys/useSiteConfig';
import { NavLink } from './navLink';
import { MenuToggle } from './menuToggle';
import { NavModal } from './navModal';
import { SearchModal } from './searchModal';
import { DonateModal } from './donateModal';

import HomeLogo from '@svg/logo-white.svg';
import HomeLogoMobile from '@svg/logo-white-m-fix.svg';
import Search from '@svg/search.svg';
import SearchMobile from '@svg/search-mobile.svg';
import SearchBlack from '@svg/search-black.svg';
import DSOwhite from '@svg/DSO-logo-white.svg';
import DSOblack from '@svg/DSO-logo-black.svg';

const navVars = {
  initial: {
    height: 0,
  },
  animate: {
    height: '350px',
    transition: {
      duration: 0.3,
    },
  },
};

const Links = ({ reverseHeader, ...item }) => {
  return (
    <div className='nav-link relative'>
      <p className='cursor-default text-base'>
        {item.topLink}
        <div className={`nav-list h-px w-full mt-2 bg-white`} />
      </p>
      <ul className='nav-list absolute pt-5 text-base leading-base font-medium w-max'>
        {item.subLinks.map((link, i) => {
          if (link.linkText === 'News & Reviews') {
            return (
              <li className='hover:opacity-50 transition duration-100 ease-in-out'>
                <Link key={i} to={`/news`}>
                  {link.linkText}
                </Link>
              </li>
            );
          } else if (link.linkText === 'Events Calendar') {
            return (
              <li className='hover:opacity-50 transition duration-100 ease-in-out'>
                <Link key={i} to={`/events`}>
                  {link.linkText}
                </Link>
              </li>
            );
          }
          return (
            <li className='hover:opacity-50 transition duration-100 ease-in-out'>
              <NavLink key={i} {...link} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const Header = ({ altLogo, settings }) => {
  const { mainNav } = useSiteConfig();
  const { reverseHeader } = settings || {};

  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  const toggleNav = () => setNavOpen((prev) => !prev);
  const toggleSearch = () => setSearchOpen((prev) => !prev);
  const toggleDonate = () => setDonateOpen((prev) => !prev);

  const [hover, setHover] = useState(false);
  const [navHover, setNavHover] = useState(false);

  return (
    <header className='flex justify-center items-center z-30 absolute top-0 w-full relative'>
      <nav
        className={`z-40 xl:z-50 flex justify-between w-full ${
          altLogo ? 'pt-5 items-start' : 'pt-0 items-center'
        } px-gutter xl:px-12 xl:pt-8`}>
        <div className='max-w-m-full-logo xl:max-w-d-full-logo w-full mt-4 xl:ml-4'>
          {altLogo ? (
            <>
              <Link to='/'>
                <HomeLogo className='hidden xl:block w-full' />
              </Link>
              <Link to='/'>
                <HomeLogoMobile className='xl:hidden' />
              </Link>
            </>
          ) : (
            <>
              {reverseHeader || navOpen || navHover || searchOpen ? (
                <Link to='/'>
                  <DSOwhite className='w-full' />
                </Link>
              ) : (
                <Link to='/'>
                  <DSOblack className='w-full' />
                </Link>
              )}
            </>
          )}
        </div>

        {/* Desktop Links */}
        <div
          className={cx('hidden xl:flex flex-col items-end font-medium', {
            'text-white': reverseHeader || navHover || searchOpen,
            'text-black': !reverseHeader && !navHover,
          })}>
          <div className='text-base'>
            <Link
              to='/concert-series-subscription'
              className={`${
                reverseHeader || navHover || searchOpen
                  ? 'hover:bg-white hover:text-black'
                  : 'hover:bg-black hover:text-white'
              } transition duration-300 ease-in-out py-1 px-6 border rounded-full inline-block`}>
              Subscribe Now
            </Link>
            <button
              onClick={toggleDonate}
              className={`${
                reverseHeader || navHover || searchOpen
                  ? 'hover:bg-white hover:text-black'
                  : 'hover:bg-black hover:text-white'
              } transition duration-300 ease-in-out py-1 px-6 border rounded-full ml-6`}>
              Donate
            </button>
            <button
              onClick={toggleSearch}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              className={`${
                reverseHeader || navHover || searchOpen
                  ? 'hover:bg-white hover:text-black'
                  : 'hover:bg-black hover:text-white'
              } transition duration-300 ease-in-out py-1 pl-6 pr-12 border rounded-full ml-6 inline-flex flex-row items-center justify-between relative`}>
              Search
              {((!reverseHeader && !hover) ||
                (hover && reverseHeader) ||
                (!reverseHeader && searchOpen && hover) ||
                (navHover && hover && !reverseHeader)) && (
                <SearchBlack className='w-5 ml-4 absolute right-4' />
              )}
              {((reverseHeader && !hover) ||
                (hover && !reverseHeader && !searchOpen && !navHover) ||
                (navHover && !reverseHeader && !hover) ||
                (!reverseHeader && searchOpen && !hover)) && (
                <Search className='w-5 ml-4 absolute right-4' />
              )}
            </button>
          </div>
          <div
            className='flex text-xl leading-xl flex-row gap-x-8 mt-3'
            onMouseEnter={() => setNavHover(true)}>
            {mainNav.map((item, i) => (
              <Links key={i} reverseHeader={reverseHeader} {...item} />
            ))}
            <Link to='/hanover-hall' className='text-base'>
              Hanover Hall
            </Link>
          </div>
        </div>

        {/* Mobile Buttons */}
        <div className='xl:hidden flex flex-row items-center gap-x-8'>
          {reverseHeader || navOpen ? (
            <>
              <SearchMobile onClick={toggleSearch} />
              <MenuToggle
                navOpen={navOpen}
                className={`w-10 h-10 focus:outline-none`}
                white={true}
                onClick={toggleNav}
              />
            </>
          ) : (
            <>
              <SearchBlack className='w-5' onClick={toggleSearch} />
              <MenuToggle
                navOpen={navOpen}
                className={`w-10 h-10 focus:outline-none`}
                white={false}
                onClick={toggleNav}
              />
            </>
          )}
        </div>
      </nav>

      <AnimatePresence exitBeforeEnter>
        <NavModal
          navOpen={navOpen}
          toggleDonate={toggleDonate}
          mainNav={mainNav}
          setNavOpen={setNavOpen}
        />
      </AnimatePresence>

      <AnimatePresence exitBeforeEnter>
        <SearchModal
          searchOpen={searchOpen}
          toggleSearch={toggleSearch}
          mainNav={mainNav}
        />
      </AnimatePresence>

      <AnimatePresence exitBeforeEnter>
        <DonateModal
          donateOpen={donateOpen}
          toggleDonate={toggleDonate}
          mainNav={mainNav}
        />
      </AnimatePresence>

      <motion.div
        variants={navVars}
        initial='initial'
        onMouseLeave={() => setNavHover(false)}
        animate={navHover ? 'animate' : 'initial'}
        exit='initial'
        className='hidden xl:block w-full absolute top-0 bg-black'
      />
    </header>
  );
};
